import React from "react";
import { $t } from "~/i18n.js";

import {
  ContactsOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { Button, Divider, Modal, Space } from "antd";

import { SluitButton } from "../buttons/SluitButton.jsx";
import { CountryLabel } from "../form/CountryCodeSelect.jsx";
import { Icon, nood } from "../icon/icon.jsx";
import { Phone } from "./phone.jsx";

const DIAL_CODES = {
  NL: "31",
  BE: "32",
  DE: "49",
  FR: "33",
  GB: "44",
  AT: "43",
  CH: "41",
};

const toE164 = (number, country) => {
  if (!number || number === -1) return null;
  const s = number.toString().replace(/\s/g, "");
  if (s.startsWith("+")) return s;
  if (s.startsWith("00")) return "+" + s.slice(2);
  if (s.startsWith("0") && DIAL_CODES[country])
    return `+${DIAL_CODES[country]}${s.slice(1)}`;
  return null;
};

const downloadVCard = (pilot, number, clubName) => {
  console.log("pilot", pilot);
  const lines = ["BEGIN:VCARD", "VERSION:3.0"];
  if (pilot?.name) lines.push(`FN:${pilot.name}`);
  if (pilot?.last_name && pilot?.first_name) {
    lines.push(`N:${pilot?.last_name || ""};${pilot?.first_name || ""};;;`);
  } else {
    lines.push(`N:${pilot?.name || ""};;;;`);
  }
  if (clubName) lines.push(`ORG:${clubName}`);
  if (number) lines.push(`TEL;TYPE=CELL:${number}`);
  if (pilot?.gegevens?.email)
    lines.push(`EMAIL;TYPE=INTERNET:${pilot.gegevens.email}`);
  const g = pilot?.gegevens;
  if (g?.address1 || g?.city || g?.zipcode) {
    const street = [g.address1, g.address2].filter(Boolean).join(" ");
    lines.push(
      `ADR;TYPE=HOME:;;${street};${g.city || ""};; ${g.zipcode || ""};${g.country || ""}`,
    );
  }
  lines.push("END:VCARD");
  const blob = new Blob([lines.join("\r\n")], {
    type: "text/vcard;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${pilot?.name || "contact"}.vcf`;
  a.click();
  URL.revokeObjectURL(url);
};

export const ContactModal = ({ pilot, gegevens, visible, onCancel, club, ziet_alle_data, currentProfile }) => {
  const clubName = club?.full_name;
  const number = pilot?.phone;
  const country = gegevens?.country;
  const e164 = toE164(number, country);

  const currentPrivateMode = currentProfile?.private_mode;

  return (
    <Modal
      title={$t("Contactgegevens")}
      open={visible}
      onCancel={onCancel}
      footer={[<SluitButton key="sluit" onClick={onCancel} />]}
    >
      <Space className='row' style={{ marginBottom: 16, flexWrap: "wrap", gap: 6 }}>
        <Button
          type="primary"
          icon={<MessageOutlined />}
          size="small"
          disabled={!e164}
          href={e164 ? `https://signal.me/#p/${e164}` : null}
          target="_blank"
          rel="noopener noreferrer"
        >
          Signal
        </Button>

        <Button
          type="ghost"
          icon={<WhatsAppOutlined />}
          size="small"
          disabled={!e164}
          href={e164 ? `https://wa.me/${e164.replace(/\D/g, "")}` : null}
          target="_blank"
          rel="noopener noreferrer"
        >
          Whatsapp
        </Button>

        <Button
          icon={<PhoneOutlined />}
          size="small"
          disabled={!number || number === -1}
          href={e164 ? `tel:${e164}` : null}
          target="_blank"
        >
          {$t("Bellen")}
        </Button>

        <Button
          icon={<ContactsOutlined />}
          size="small"
          disabled={!number || number === -1}
          onClick={() => downloadVCard(pilot, number, clubName)}
        >
          {$t("Bewaar contact")}
        </Button>
      </Space>

      <div className="row">
        <Button
          type="primary"
          icon={<MailOutlined />}
          size="small"
          shape="circle"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          target="_blank"
          href={gegevens ? `mailto:${gegevens.email}` : null}
        />
        <p style={{ marginLeft: 6, marginRight: 18 }}>
          {gegevens ? gegevens.email : $t("onbekend")}
        </p>
      </div>

      {gegevens && gegevens.address1 && !currentPrivateMode && (ziet_alle_data || !pilot.private_mode) ? (
        <>
          <p>{gegevens.address1}</p>
          {gegevens.address2 && <p>{gegevens.address2}</p>}
          <p>
            {gegevens.zipcode}, {gegevens.city}
          </p>
          <p>
            <CountryLabel code={gegevens.country} />
          </p>
        </>
      ) : (
        <p>{$t("Woonadres onbekend/afgeschermd")}</p>
      )}

      <Divider />

      <p>
        <Icon icon={nood} viewBox="0 0 1124 1024" />{" "}
        {$t("Contactpersoon in geval van nood")}
      </p>

      {gegevens && gegevens.emergency_contact && !currentPrivateMode && (ziet_alle_data || !pilot.private_mode) ? (
        <div>
          <p style={{ margin: "12px 0" }}>
            <span className="bold">{gegevens.emergency_contact}</span> (
            {gegevens.emergency_contact_relation})
          </p>
          <Phone number={gegevens.emergency_contact_phone} />
          <Button
            icon={<PhoneOutlined />}
            size="small"
            disabled={
              !gegevens.emergency_contact_phone ||
              gegevens.emergency_contact_phone === -1
            }
            href={
              gegevens.emergency_contact_phone
                ? `tel:${gegevens.emergency_contact_phone}`
                : null
            }
            target="_blank"
          >
            {$t("Bellen")}
          </Button>
        </div>
      ) : (
        <p>{$t("Onbekend/afgeschermd")}</p>
      )}
    </Modal>
  );
};
